import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { HarrisonPromptVersion } from '../domain/entities/harrison.entity';
import type { OfficialCommentOrigin } from '../domain/entities/content.entity';
import { sanitizeRichText } from '@react-apps/content-editor';

/**
 * O que a tela do Harrison faz com texto (DESEN-34): o rascunho chega em
 * markdown e vai para o editor do comentário; o prompt chega como HTML (ou
 * texto puro, nas versões antigas) e vira resumo, visualização e editor.
 */

/**
 * Markdown do Harrison → HTML do editor do comentário. Passa pelo
 * `sanitizeRichText`, o mesmo allowlist do back: o texto vem de um LLM, e o que
 * não cabe no comentário (imagem, tabela, script) não entra no editor.
 */
export function markdownToEditorHtml(markdown: string): string {
  // `async: false` faz o `parse` síncrono; o tipo do `marked` não estreita.
  const html = marked.parse(markdown, {
    async: false,
    gfm: true,
    breaks: true,
  }) as string;
  return sanitizeRichText(html);
}

const HTML_TAG = /<\/?[a-z][\s\S]*?>/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * HTML seguro do prompt, para ver e para editar. Texto puro vira um parágrafo
 * por bloco. O HTML vem do Harrison, e qualquer um com a chave dele grava ali:
 * por isso sempre sanitizado antes de chegar à tela.
 */
export function promptToHtml(content: string): string {
  const html = HTML_TAG.test(content)
    ? content
    : content
        .split(/\n{2,}/)
        .map((block) => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
        .join('');
  return sanitizeRichText(html);
}

/** Texto corrido para o resumo do card do histórico ("PERSONA Você é…"). */
export function promptPreview(content: string): string {
  if (!HTML_TAG.test(content)) return content.replace(/\s+/g, ' ').trim();
  const spaced = content.replace(
    /<\/(p|h[1-6]|li|div|blockquote)>|<br\s*\/?>/gi,
    ' $&',
  );
  const text = DOMPurify.sanitize(spaced, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
  const doc = new DOMParser().parseFromString(text, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
}

/** Nome da versão: o título, ou o número quando não há título. */
export function promptLabel(version: HarrisonPromptVersion): string {
  return version.title?.trim() || `Versão ${version.version}`;
}

const DATE_TIME = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export function formatPromptDate(iso: string | null): string {
  if (!iso) return 'sem data';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? 'sem data' : DATE_TIME.format(date);
}

/**
 * Mensagem de falha das rotas do Harrison. Lê o código que o back devolve em
 * `error` (`HARRISON_UNAVAILABLE`) e o status, nunca o `message`: ele cita
 * motivo técnico (`timeout`) e não foi escrito para a tela.
 */
export function harrisonErrorMessage(
  err: unknown,
  action: 'gerar' | 'salvar' | 'tornar padrão' | 'carregar',
): string {
  const response = (
    err as { response?: { status?: number; data?: { error?: unknown } } }
  )?.response;
  if (!response) {
    return `Não foi possível ${action}: sem conexão com o servidor. Tente de novo.`;
  }
  if (
    response.data?.error === 'HARRISON_UNAVAILABLE' ||
    response.status === 503
  ) {
    return action === 'gerar'
      ? 'O Harrison não respondeu. Tente de novo ou escreva o comentário.'
      : `O Harrison não respondeu e nada foi ${action === 'carregar' ? 'carregado' : 'alterado'}. Tente de novo.`;
  }
  if (response.status === 403) return 'Você não tem acesso ao Harrison.';
  if (response.status === 404) {
    if (action === 'tornar padrão') {
      return 'Esta versão não existe mais. Recarregue o histórico.';
    }
    if (action === 'gerar') return 'Questão não encontrada.';
  }
  if (response.status === 400 && action === 'gerar') {
    return 'Não dá para gerar o rascunho desta questão: ela não tem gabarito, ou o modelo não é aceito.';
  }
  return `Não foi possível ${action}. Tente de novo.`;
}

/** A origem que vai no `PUT` do comentário (`origin` do audit). */
export type CommentOrigin = OfficialCommentOrigin;

/**
 * `HARRISON` enquanto houver texto de uma geração aplicada; `ADMIN_FORM` se a
 * pessoa nunca gerou, ou zerou o editor depois de gerar. Editar o rascunho não
 * muda a origem: a garantia é "houve geração aplicada e o texto não foi
 * zerado", não "o texto é exatamente o do Harrison".
 */
export function commentOrigin(state: {
  draftApplied: boolean;
  editorEmpty: boolean;
}): CommentOrigin {
  return state.draftApplied && !state.editorEmpty ? 'HARRISON' : 'ADMIN_FORM';
}

/**
 * A versão em uso sempre primeiro, e as outras na ordem que vieram (da mais
 * recente para a mais antiga). O back já devolve assim; reordenar aqui mantém
 * a regra mesmo entre a troca do padrão e a releitura do histórico.
 */
export function orderPrompts(
  versions: HarrisonPromptVersion[],
): HarrisonPromptVersion[] {
  return [
    ...versions.filter((v) => v.isDefault),
    ...versions.filter((v) => !v.isDefault),
  ];
}

/**
 * Os modelos que a tela oferece: só as linhas **Terra** e **Luna**, pelo sufixo
 * do id (`gpt-5.6-terra`, `gpt-5.6-luna`). Decisão de produto só do admin: o
 * Harrison e o back seguem aceitando todos, e a lista que vem deles é a fonte
 * oferece a primeira versão disponível de cada família, na ordem do backend,
 * sem repetir famílias nem criar opções ausentes na resposta.
 */
const SELECTABLE_MODEL_LINES = ['terra', 'luna'];

export function selectableModels(models: string[]): string[] {
  const seen = new Set<string>();
  return models.filter((id) => {
    const family = id.split('-').at(-1)?.toLowerCase() ?? '';
    if (!SELECTABLE_MODEL_LINES.includes(family) || seen.has(family))
      return false;
    seen.add(family);
    return true;
  });
}

/**
 * Nome de exibição do modelo: o id do Harrison (`gpt-5.6-terra`) vira
 * `GPT-5.6 Terra`. Só exibição; o que vai ao back é sempre o id.
 */
export function modelLabel(id: string): string {
  const [family, version, ...rest] = id.split('-');
  if (!family) return id;
  const head = version
    ? `${family.toUpperCase()}-${version}`
    : family.toUpperCase();
  const tail = rest.map((w) => w.charAt(0).toUpperCase() + w.slice(1));
  return [head, ...tail].join(' ');
}
